import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Alert from '@amzn/awsui-components-console/alert';
import Box from '@amzn/awsui-components-console/box';
import Button from '@amzn/awsui-components-console/button';
import FormField from '@amzn/awsui-components-console/form-field';
import Modal from '@amzn/awsui-components-console/modal';
import Multiselect, {
  MultiselectProps,
} from '@amzn/awsui-components-console/multiselect';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import Textarea from '@amzn/awsui-components-console/textarea';
import { useDescribeCxFamilyQuery } from 'src/generated/graphql';
import { GroupEdits, StringGroup } from '../types';

const DESCRIPTION_CHAR_LIMIT = 500;

interface EditGroupModalProps {
  visible: boolean;
  group: StringGroup;
  draftEdits?: GroupEdits;
  onSave: (edits: GroupEdits) => void;
  onDismiss: () => void;
}

const EditGroupModal: React.FC<EditGroupModalProps> = ({
  visible,
  group,
  draftEdits,
  onSave,
  onDismiss,
}) => {
  const [description, setDescription] = useState('');
  const [selectedCxIds, setSelectedCxIds] = useState<MultiselectProps.Option[]>(
    [],
  );
  const [showConfirmation, setShowConfirmation] = useState(false);

  const {
    data: cxFamilyData,
    loading: cxIdsLoading,
    error: cxIdsError,
  } = useDescribeCxFamilyQuery({
    variables: { cxFamilyId: group.cxFamilyId },
    skip: !visible || !group.cxFamilyId,
  });

  const cxIdOptions = useMemo<MultiselectProps.Option[]>(
    () =>
      (cxFamilyData?.describeCxFamily.cxFamily.cxIds ?? []).map(({ cxId }) => ({
        label: cxId,
        value: cxId,
      })),
    [cxFamilyData],
  );

  const effectiveDescription =
    draftEdits?.description ?? group.description ?? '';
  const effectiveCxIds = draftEdits?.cxIds ?? group.cxIds;

  // Keep refs to the latest effective values so the effect can read them
  // without listing them as dependencies (which would re-fire mid-edit).
  const effectiveDescriptionRef = useRef(effectiveDescription);
  effectiveDescriptionRef.current = effectiveDescription;
  const effectiveCxIdsRef = useRef(effectiveCxIds);
  effectiveCxIdsRef.current = effectiveCxIds;

  const prevVisibleRef = useRef(false);

  // Baseline snapshot captured at open time — the exact values the form was
  // initialised from. `cxIdsChanged()` and the save-time no-op guard compare
  // against THIS frozen snapshot rather than the live effective values, so a
  // background refetch that lands while the modal is open can't make the
  // save-time baseline diverge from what the user actually sees. Comparing
  // against live props instead would spuriously trip the confirmation dialog
  // and could silently save stale field values over newer server data.
  const baselineDescriptionRef = useRef(effectiveDescription);
  const baselineCxIdsRef = useRef(effectiveCxIds);

  // Re-initialise form state only on the visible false→true transition.
  // A saved modal edit belongs to the local draft until the whole group is
  // submitted, so reopening must use draft values ahead of server values.
  // We intentionally ignore prop changes while the modal is already open —
  // overwriting the user's in-progress edits (e.g. because a background
  // refetch landed a new array reference) is worse than showing stale
  // initial values; the user is actively editing and their input takes
  // precedence.
  useEffect(() => {
    const wasVisible = prevVisibleRef.current;
    prevVisibleRef.current = visible;

    if (visible && !wasVisible) {
      const initialDescription = effectiveDescriptionRef.current;
      const initialCxIds = effectiveCxIdsRef.current;
      // Freeze the baseline used by change-detection/save to the values the
      // form is being seeded with, so they stay in lockstep.
      baselineDescriptionRef.current = initialDescription;
      baselineCxIdsRef.current = initialCxIds;
      setDescription(initialDescription);
      setSelectedCxIds(initialCxIds.map((id) => ({ label: id, value: id })));
      setShowConfirmation(false);
    }
  }, [visible]);

  const cxIdsChanged = useCallback((): boolean => {
    const currentSet = new Set(baselineCxIdsRef.current);
    const selectedSet = new Set(
      selectedCxIds.map((opt) => opt.value).filter(Boolean) as string[],
    );
    if (currentSet.size !== selectedSet.size) return true;
    for (const id of currentSet) {
      if (!selectedSet.has(id)) return true;
    }
    return false;
  }, [selectedCxIds]);

  const handleSave = useCallback(() => {
    if (cxIdsChanged()) {
      setShowConfirmation(true);
      return;
    }
    if (description === baselineDescriptionRef.current) {
      onDismiss();
      return;
    }
    onSave({
      description,
      cxIds: selectedCxIds.map((opt) => opt.value).filter(Boolean) as string[],
    });
    onDismiss();
  }, [cxIdsChanged, description, selectedCxIds, onSave, onDismiss]);

  const handleConfirm = useCallback(() => {
    onSave({
      description,
      cxIds: selectedCxIds.map((opt) => opt.value).filter(Boolean) as string[],
    });
    setShowConfirmation(false);
    onDismiss();
  }, [description, selectedCxIds, onSave, onDismiss]);

  const handleConfirmCancel = useCallback(() => {
    setShowConfirmation(false);
  }, []);

  return (
    <>
      <Modal
        visible={visible && !showConfirmation}
        onDismiss={onDismiss}
        header="Edit String Group"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={onDismiss}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleSave}>
                Save Changes
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="l">
          <FormField label="Name">
            <Box fontWeight="bold" fontSize="body-m">
              {group.stringGroupId}
            </Box>
          </FormField>

          <FormField label="CX Family">
            <Box color="text-body-secondary">{group.cxFamilyId}</Box>
          </FormField>

          <FormField
            label="Description"
            constraintText={`Character limit: ${DESCRIPTION_CHAR_LIMIT}`}
          >
            <Textarea
              value={description}
              onChange={({ detail }) =>
                setDescription(detail.value.slice(0, DESCRIPTION_CHAR_LIMIT))
              }
              placeholder="Enter a description for this string group."
            />
          </FormField>

          <FormField
            label="Select CX IDs"
            constraintText="Hold Ctrl/Cmd to select multiple. Changing CXIDs affects target locales."
          >
            <Multiselect
              selectedOptions={selectedCxIds}
              onChange={({ detail }) =>
                setSelectedCxIds([...detail.selectedOptions])
              }
              options={cxIdOptions}
              filteringType="auto"
              filteringPlaceholder="Find CX ID"
              statusType={
                cxIdsError ? 'error' : cxIdsLoading ? 'loading' : 'finished'
              }
              loadingText="Loading CX IDs"
              errorText="Unable to load CX IDs."
              empty="No CX IDs available"
              disabled={cxIdsLoading || !!cxIdsError}
              placeholder="Select CX IDs"
            />
          </FormField>

          <FormField label="Team Name">
            <Box color="text-body-secondary">{group.teamName ?? '—'}</Box>
          </FormField>

          <FormField label="Source Locale">
            <Box color="text-body-secondary">{group.sourceLocale}</Box>
          </FormField>
        </SpaceBetween>
      </Modal>

      {/* Confirmation sub-modal for CX ID changes */}
      <Modal
        visible={showConfirmation}
        onDismiss={handleConfirmCancel}
        header="Confirm Changes"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={handleConfirmCancel}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleConfirm}>
                Confirm
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <Alert type="warning">
          If you have changed the CXIDs, it will impact the locales to which
          your current strings in the group are translated to.
        </Alert>
      </Modal>
    </>
  );
};

export default EditGroupModal;
